import { useCallback, useState } from "react";
import { useToast } from "@chakra-ui/react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  DeliveryOption,
  EnrichedSupplierForDelivery,
  SelectedDeliveryOption,
} from "../../types";
import { useCreateManualDeliveryOption } from "../../../../views/Orders/hooks/useCreateManualDeliveryOption";
import { useDeleteDeliveryOption } from "../../../../views/Orders/hooks/useDeleteDeliveryOption";
import { CPTSlabForManualOption } from "../../../../views/Orders/enums";

dayjs.extend(utc);
dayjs.extend(timezone);

interface UseEstimateSplitMutationsParams {
  estimateNo: string;
  supplier: EnrichedSupplierForDelivery;
  selection: SelectedDeliveryOption | undefined;
  onSelectionChange: (selection: SelectedDeliveryOption | undefined) => void;
  onOptionDelete: (optionId: string) => void;
  onOptionAdd: (newOption: DeliveryOption) => void;
  directDeliveryMinimumDeliveryCost?: number;
  isManualSelected: boolean;
  isExpired: boolean;
}

export function useEstimateSplitMutations({
  estimateNo,
  supplier,
  selection,
  onSelectionChange,
  onOptionDelete,
  onOptionAdd,
  directDeliveryMinimumDeliveryCost,
  isManualSelected,
  isExpired,
}: UseEstimateSplitMutationsParams) {
  const toast = useToast();
  const [manualSaveError, setManualSaveError] = useState<string | null>(null);

  const { mutateAsync: createManualOption, isLoading: isSavingManualOption } =
    useCreateManualDeliveryOption();

  const { mutateAsync: deleteDeliveryOption, isLoading: isDeletingOption } =
    useDeleteDeliveryOption();

  const isDeleteDisabled =
    isExpired || isSavingManualOption || isDeletingOption;

  const handleOptionDelete = useCallback(
    async (optionId: string) => {
      if (isDeleteDisabled) return;

      try {
        await deleteDeliveryOption({
          estimateNo,
          splitId: supplier.splitId,
          optionId,
        });

        if (
          selection?.customerAction === "confirm" &&
          selection?.deliveryDetails?.selectionType === "option" &&
          selection.deliveryDetails.selectedDeliveryOptionId === optionId
        ) {
          onSelectionChange(undefined);
        }

        onOptionDelete(optionId);
      } catch (e: unknown) {
        toast({
          title: "Failed to delete delivery option",
          description:
            e instanceof Error ? e.message : "Please try again.",
          status: "error",
          duration: 3000,
          position: "top",
        });
      }
    },
    [
      isDeleteDisabled,
      deleteDeliveryOption,
      estimateNo,
      supplier.splitId,
      supplier.deliveryOptions,
      selection,
      onSelectionChange,
      onOptionDelete,
      toast,
    ],
  );

  const handleManualEntrySave = useCallback(async () => {
    if (isExpired) return;
    if (!isManualSelected) return;
    if (isSavingManualOption) return;

    setManualSaveError(null);

    const entry = selection?.deliveryDetails?.manualEntry;
    if (!entry?.cpt || !entry?.cptSlab) {
      setManualSaveError("Please select CPT and CPT slab before saving.");
      return;
    }

    const deliveryCharge = entry.deliveryCharge;
    if (
      deliveryCharge === undefined ||
      deliveryCharge === null ||
      Number.isNaN(Number(deliveryCharge))
    ) {
      setManualSaveError("Please enter a valid delivery charge before saving.");
      return;
    }

    const minCharge =
      entry.cptSlab === CPTSlabForManualOption.DIRECT_DELIVERY
        ? (directDeliveryMinimumDeliveryCost ?? 50)
        : 0;

    if (
      entry.cptSlab !== CPTSlabForManualOption.SELF_PICKUP &&
      Number(deliveryCharge) < minCharge
    ) {
      setManualSaveError(
        `Delivery charge must be at least ₹${minCharge} for Direct Delivery.`,
      );
      return;
    }

    const cptUtc = dayjs.tz(entry.cpt, "Asia/Kolkata").utc().toISOString();

    try {
      const resp = await createManualOption({
        estimateNo,
        splitId: supplier.splitId,
        payload: {
          cptMin: cptUtc,
          cptMax: cptUtc,
          cptSlab: entry.cptSlab,
          deliveryCost: Number(deliveryCharge),
        },
      });

      const createdDeliveryOption = {
        ...resp.deliveryOption,
        cptSlab: Number(resp.deliveryOption.cptSlab),
      } as DeliveryOption;

      onOptionAdd(createdDeliveryOption);

      // Select by stable _id from the API response — not array index (avoids stale-length bugs).
      onSelectionChange({
        supplierId: supplier.supplierId,
        customerAction: "confirm",
        deliveryDetails: {
          selectionType: "option",
          selectedDeliveryOptionId: createdDeliveryOption._id,
        },
      });
    } catch (e: unknown) {
      setManualSaveError(
        e instanceof Error
          ? e.message
          : "Failed to save manual delivery option.",
      );
    }
  }, [
    isExpired,
    isManualSelected,
    isSavingManualOption,
    selection,
    directDeliveryMinimumDeliveryCost,
    createManualOption,
    estimateNo,
    supplier.splitId,
    supplier.supplierId,
    onSelectionChange,
    onOptionAdd,
  ]);

  return {
    handleOptionDelete,
    handleManualEntrySave,
    isSavingManualOption,
    isDeletingOption,
    isDeleteDisabled,
    manualSaveError,
    setManualSaveError,
  };
}
